import React, {useState, useEffect, useRef} from 'react';
import {formatLinksInText} from "../../services/helpers";
import DOMPurify from "dompurify";

const LinkFormatterInput = ({placeholder, value, onChange, inputRef, disabled}) => {
    const timeoutRef = useRef(null);
    const lastOnChangeValue = useRef(null);
    const [showPlaceholder, setShowPlaceholder] = useState(true);

    // Counts characters from start of element to the caret position.
    // Treats <div> block boundaries and <br> elements as 1 character (newline),
    // matching the behavior of innerText.
    const getCaretOffset = (element) => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return 0;

        const range = selection.getRangeAt(0);
        const targetNode = range.startContainer;
        const targetNodeOffset = range.startOffset;

        let count = 0;
        let found = false;

        const walk = (node) => {
            if (found) return;

            if (node.nodeType === Node.TEXT_NODE) {
                if (node === targetNode) {
                    count += targetNodeOffset;
                    found = true;
                    return;
                }
                count += node.textContent.length;
            } else if (node.nodeName === 'BR') {
                count += 1;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const isBlock = node !== element && node.nodeName === 'DIV';
                if (isBlock && count > 0) {
                    count += 1;
                }

                if (node === targetNode) {
                    for (let i = 0; i < targetNodeOffset && i < node.childNodes.length; i++) {
                        walk(node.childNodes[i]);
                        if (found) return;
                    }
                    found = true;
                    return;
                }

                for (const child of node.childNodes) {
                    walk(child);
                    if (found) return;
                }
            }
        };

        walk(element);
        return count;
    };

    // Walks the formatted DOM (text nodes, <br>, <a> wrappers) and places
    // the caret at the given character offset.
    const setCaretOffset = (element, targetOffset) => {
        let count = 0;
        let placed = false;

        const walk = (node) => {
            if (placed) return;

            if (node.nodeType === Node.TEXT_NODE) {
                const len = node.textContent.length;
                if (count + len >= targetOffset) {
                    const range = document.createRange();
                    range.setStart(node, targetOffset - count);
                    range.collapse(true);
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                    placed = true;
                    return;
                }
                count += len;
            } else if (node.nodeName === 'BR') {
                if (count >= targetOffset) {
                    const range = document.createRange();
                    const parent = node.parentNode;
                    const index = Array.from(parent.childNodes).indexOf(node);
                    range.setStart(parent, index);
                    range.collapse(true);
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                    placed = true;
                    return;
                }
                count += 1;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                for (const child of node.childNodes) {
                    walk(child);
                    if (placed) return;
                }
            }
        };

        walk(element);
        if (!placed) {
            placeCaretAtEnd(element);
        }
    };

    const handleInput = () => {
        const div = inputRef.current;
        const text = div.innerText;

        if (text.trim() === "") {
            setShowPlaceholder(true);
            div.innerHTML = "";
        } else {
            setShowPlaceholder(false);
            const caretPos = getCaretOffset(div);
            div.innerHTML = formatLinksInText(text, false).replace(/\n/g, '<br>');
            setCaretOffset(div, caretPos);
        }

        const sanitizedText = DOMPurify.sanitize(text);
        lastOnChangeValue.current = sanitizedText;
        onChange(sanitizedText);
    };

    const placeCaretAtEnd = (element) => {
        const range = document.createRange();
        const selection = window.getSelection();

        range.selectNodeContents(element);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    const debounceHandleInput = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            handleInput();
        }, 300);
    };

    useEffect(() => {
            if (value === lastOnChangeValue.current) {
                lastOnChangeValue.current = null;
                return;
            }

            const div = inputRef.current;
            div.innerHTML = formatLinksInText(value).replace(/\n/g, '<br>');

            // Toggle placeholder visibility on mount
            if (value.trim() === "") {
                setShowPlaceholder(true);
            } else {
                setShowPlaceholder(false);
            }

            placeCaretAtEnd(div);
        },
        /* eslint-disable react-hooks/exhaustive-deps */
        [value]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (<div
            ref={inputRef}
            contentEditable={!disabled}
            onInput={debounceHandleInput}
            className={`sl-input sl-text-left sl-h-25 sl-modal-textarea sl-border-1 sl-shadow-xs sl-rounded-lg sl-font sl-font-normal sl-text-md sl-text-gray-base sl-customized-scrollbar overflow-y-auto ${showPlaceholder ? 'sl-placeholder' : ''}`}
            data-placeholder={placeholder}
        />
    );
}

export default LinkFormatterInput;
